/**
 * Hooks TanStack Query per FASE 7 (listone + privacy).
 *
 * Privacy 4 livelli (iter 2):
 *   GET    /squadre                          → lista squadre
 *   GET    /squadre/{id}/privacy             → livello corrente
 *   PATCH  /squadre/{id}/privacy             → modifica livello
 *   GET    /squadre/{id}/visibility          → visibilità aree per viewer
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";
import type { MagliaPattern } from "../features/common/Maglia";

export type LivelloPrivacy = "pubblico" | "lega" | "riservato" | "privato";
export type Visibility = "visibile" | "aggregato" | "nascosto";

export interface SquadraItem {
  id: string;
  nome: string;
  presidente_user_id: string | null;
  data_ingresso_lega: string | null;
  data_uscita_lega: string | null;
  livello_privacy: LivelloPrivacy;
  numero_cambi_nome: number;
  // Andrea 2026-05-20 — logo personalizzato data-URL. null = mostrare iniziali.
  logo_data_url: string | null;
  // Andrea 2026-06-11 — maglia personalizzata (SVG parametrico).
  maglia_pattern: MagliaPattern;
  maglia_colore_corpo: string;
  maglia_colore_dettaglio: string;
  maglia_colore_maniche: string;
  // Andrea 2026-10-07 — template nuovo: 5 colori
  maglia_colore_accento?: string;
  maglia_colore_colletto?: string;
  maglia_colore_polsini?: string;
  maglia_colore_sponsor?: string | null;
  maglia_sponsor?: string;
  // toppe: automatiche per il vincitore in carica (calcolate dal server)
  maglia_patch_scudetto: boolean;
  maglia_patch_champions: boolean;
  maglia_patch_supercoppa: boolean;
}

export interface MagliaPayload {
  maglia_pattern: MagliaPattern;
  maglia_colore_corpo: string;
  maglia_colore_dettaglio: string;
  maglia_colore_maniche: string;
  maglia_colore_accento: string;
  maglia_colore_colletto: string;
  maglia_colore_polsini: string;
  maglia_colore_sponsor: string | null;
  maglia_sponsor: "logo_nome" | "logo";
}

export interface DirittiPatch {
  scudetto: boolean;
  champions: boolean;
  supercoppa: boolean;
}

export interface PrivacyResponse {
  squadra_id: string;
  livello_privacy: LivelloPrivacy;
}

export interface VisibilityResponse {
  squadra_id: string;
  viewer_role: "self" | "admin" | "partecipante" | "guest";
  livello_privacy: LivelloPrivacy;
  rosa: Visibility;
  tesoreria: Visibility;
  storico: Visibility;
}

export const PRIVACY_LABEL: Record<LivelloPrivacy, string> = {
  pubblico: "Pubblico — tutto visibile a chiunque",
  lega: "Lega — rosa visibile, tesoreria+storico aggregati (default)",
  riservato: "Riservato — solo conteggi aggregati",
  privato: "Privato — nulla visibile agli altri partecipanti",
};

export function useSquadre() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["squadre"],
    queryFn: async () => {
      const res = await api.get<SquadraItem[]>("/squadre");
      return res.data;
    },
    enabled: isAuth,
  });
}

export function useSquadraPrivacy(squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["squadre", "privacy", squadraId],
    queryFn: async () => {
      const res = await api.get<PrivacyResponse>(`/squadre/${squadraId}/privacy`);
      return res.data;
    },
    enabled: isAuth && !!squadraId,
  });
}

export function useSquadraVisibility(squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["squadre", "visibility", squadraId],
    queryFn: async () => {
      const res = await api.get<VisibilityResponse>(`/squadre/${squadraId}/visibility`);
      return res.data;
    },
    enabled: isAuth && !!squadraId,
  });
}

export function useUpdateLogoSquadra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { squadraId: string; logoDataUrl: string | null }) => {
      const res = await api.patch<SquadraItem>(`/squadre/${args.squadraId}/logo`, {
        logo_data_url: args.logoDataUrl,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["squadre"] });
    },
  });
}

export function useUpdateMagliaSquadra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { squadraId: string; maglia: MagliaPayload }) => {
      const res = await api.patch<SquadraItem>(`/squadre/${args.squadraId}/maglia`, args.maglia);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["squadre"] });
    },
  });
}

/** Per quali patch trofeo la squadra ha diritto (vincitore ultima conclusa). */
export function useDirittiPatch(squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["squadre", "diritti-patch", squadraId],
    queryFn: async () => {
      const res = await api.get<DirittiPatch>(`/squadre/${squadraId}/maglia/diritti-patch`);
      return res.data;
    },
    enabled: isAuth && !!squadraId,
  });
}

// ============================================================
// Allenatori in seconda (Andrea 2026-05-21)
// ============================================================
export interface AllenatoreSecondoItem {
  id: string;
  user_id: string;
  user_email: string;
  user_nome_completo: string;
  invitato_da_user_id: string | null;
  data_accettazione: string;
}

export interface InvitaAllenatoreSecondoResponse {
  invito_id: string;
  codice_leggibile: string;
  link_invito: string;
  email_destinatario: string;
  scadenza: string;
}

export function useAllenatoriSecondi(squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["allenatori-secondi", squadraId],
    queryFn: async () => {
      const res = await api.get<AllenatoreSecondoItem[]>(
        `/squadre/${squadraId}/allenatori-secondi`,
      );
      return res.data;
    },
    enabled: isAuth && !!squadraId,
  });
}

export function useInvitaAllenatoreSecondo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      squadraId: string;
      emailAllenatore: string;
      scadenzaGiorni?: number;
    }) => {
      const res = await api.post<InvitaAllenatoreSecondoResponse>(
        `/squadre/${args.squadraId}/invita-secondo`,
        {
          email_allenatore: args.emailAllenatore,
          scadenza_giorni: args.scadenzaGiorni ?? 7,
        },
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["allenatori-secondi", vars.squadraId],
      });
    },
  });
}

// Andrea 2026-10-08: cessione della squadra via invito
export interface CessioneSquadra {
  invito_id: string;
  email_destinatario: string;
  codice_leggibile: string;
  link_invito: string;
  scadenza: string;
  created_at: string;
}

export function useCessioneSquadra(squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["cessione-squadra", squadraId],
    queryFn: async () =>
      (await api.get<CessioneSquadra | null>(`/squadre/${squadraId}/cessione`)).data,
    enabled: isAuth && !!squadraId,
  });
}

export function useCediSquadra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { squadraId: string; email: string }) =>
      (
        await api.post<CessioneSquadra>(`/squadre/${args.squadraId}/cessione`, {
          email_nuovo_presidente: args.email,
        })
      ).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["cessione-squadra", vars.squadraId] });
    },
  });
}

export function useAnnullaCessione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { squadraId: string }) => {
      await api.delete(`/squadre/${args.squadraId}/cessione`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["cessione-squadra", vars.squadraId] });
    },
  });
}

export function useRimuoviAllenatoreSecondo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { squadraId: string; userId: string }) => {
      await api.delete(`/squadre/${args.squadraId}/allenatori-secondi/${args.userId}`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["allenatori-secondi", vars.squadraId],
      });
    },
  });
}

/** Hook unico per "le mie squadre" — risolve sia presidente diretto
 * che allenatore in seconda. Sostituisce il pattern
 * `squadre.filter(s => s.presidente_user_id === user.id)` sparso in
 * piu' pagine. Andrea 2026-05-21. */
export function useMieSquadre(currentUserId: string | undefined) {
  const { data: squadre, isLoading: loadingSquadre } = useSquadre();
  const { data: idsGestite, isLoading: loadingGestite } = useMieSquadreGestite();
  const mie = (squadre ?? []).filter(
    (s) => s.presidente_user_id === currentUserId || (idsGestite ?? []).includes(s.id),
  );
  return {
    data: mie,
    isLoading: loadingSquadre || loadingGestite,
  };
}

/** Lista IDs squadre che l'utente corrente puo' "gestire" come manager
 * sportivo (presidente diretto OR allenatore in seconda). Serve a
 * RosaPage/FormazionePage/MercatoPage per riconoscere "le mie squadre"
 * quando si e' partecipante invitato come socio. */
export function useMieSquadreGestite() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["mie-squadre-gestite"],
    queryFn: async () => {
      const res = await api.get<string[]>("/squadre/me/squadre-gestite");
      return res.data;
    },
    enabled: isAuth,
  });
}

export function useUpdatePrivacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { squadraId: string; livello: LivelloPrivacy }) => {
      const res = await api.patch<PrivacyResponse>(`/squadre/${args.squadraId}/privacy`, {
        livello_privacy: args.livello,
      });
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["squadre"] });
      qc.invalidateQueries({ queryKey: ["squadre", "privacy", vars.squadraId] });
      qc.invalidateQueries({ queryKey: ["squadre", "visibility", vars.squadraId] });
      qc.invalidateQueries({ queryKey: ["rosa"] });
      qc.invalidateQueries({ queryKey: ["budget"] });
    },
  });
}
